import React, { PropsWithChildren, ReactNode, useState, useEffect } from 'react';
import { ArrowDarkIcon, DoneIcon } from './svg/Icons';
import { Button, Container, Alert } from 'react-bootstrap';
import { DocBooleanField } from '../common/doc/fields/DocBooleanField';
import { helpPhone1, helpPhone2 } from '../model/Const';
import { prettyPrintPLPhoneNumber } from '../common/utl/PhoneNumberUtils';
import { usePathInfo } from '../common/routes/ErrorContext';

const phStyle = {
  fontSize: 25,
  letterSpacing: 0,
  lineHeight: '35px',
  fontWeight: 600,
  margin: '2rem 0',
};

export function PageHeader(props: PropsWithChildren<{ className?: string }>)
{
  return (
    <h2 {...props} style={phStyle} />
  );
}

const formHeaderStyle = {
  fontSize: 18,
  letterSpacing: 0,
  lineHeight: '35px',
  fontWeight: 600,
};

const formNoteStyle = {
  fontSize: 14,
  letterSpacing: 0,
  lineHeight: '20px',
  fontWeight: 100,
};

export const font16Style = {
  fontSize: 16,
  letterSpacing: 0,
  lineHeight: '35px',
  fontWeight: 400,
};

export const font18Style = {
  fontSize: 18,
  letterSpacing: 0,
  lineHeight: '40px',
  fontWeight: 500,
};

export function FormHeader(props: PropsWithChildren<{ id?: string, className?: string }>)
{
  return (
    <div {...props} style={formHeaderStyle} />
  );
}

export function FormNote(props: PropsWithChildren<{ id?: string, className?: string }>)
{
  return (
    <div {...props} style={formNoteStyle} />
  );
}

export interface CheckBoxProps
{
  checked: boolean;
  onCheck?: () => void;
  invalid?: boolean;
  alignTop?: boolean;
  tooltip?: ReactNode;
  id?: string;
  className?: string;
}

export function CheckBox(props: PropsWithChildren<CheckBoxProps>)
{
  const alignItems = !props.alignTop ? 'center' : 'start';
  const style = { alignItems };
  return (
    <div className="checkbox-tick-outer" style={style}>
      <div className="checkbox-tick-panel mr-2">
        <div
          id={props.id}
          className={`checkbox-tick${props.invalid ? ' is-invalid' : ''}`}
          onClick={props.onCheck}
        >
          {props.checked &&
            <DoneIcon style={{ width: 17.96, height: 13.05, fill: 'var(--blue-color)' }} />
          }
        </div>
      </div>
      <div className="checkbox-text-panel">
        {props.children}
      </div>
    </div>
  );
}

export function RadioBox(props: PropsWithChildren<CheckBoxProps>)
{
  const classes = [props.className, 'radio-button'];
  if (props.invalid)
  {
    classes.push('is-invalid');
  }

  if (props.checked)
  {
    classes.push('is-checked');
  }

  return (
    <Button
      className={classes.join(' ')}
      onClick={props.onCheck}
    >
      {props.children}
    </Button>
  );
}

export function RadioBoolean(props:
  {
    docField: DocBooleanField,
    onChange?: (checked: boolean) => void
  })
{
  const { docField, onChange } = props;

  const pathInfo = usePathInfo();
  useEffect(() =>
  {
    pathInfo.setPath(docField.uniqueId, { caption: docField.fieldLabel });
  }, [docField.fieldLabel, docField.uniqueId]);

  return <div className="d-flex my-2" id={docField.uniqueId}>
    <RadioBox
      checked={docField.asBool}
      className="blue"
      onCheck={
        () =>
        {
          docField.setValue(true);
          if (onChange)
          {
            onChange(true);
          }
        }
      }
    >
      TAK
    </RadioBox>
    <div className="ml-5" />
    <RadioBox
      checked={docField.getValue() === false}
      className="red"
      onCheck={
        () =>
        {
          docField.setValue(false);
          if (onChange)
          {
            onChange(false);
          }
        }
      }
    >
      NIE
    </RadioBox>
  </div>;
}

export function ButtonLink(props: PropsWithChildren<{ href: string }>)
{
  return (
    <Button
      id="link"
      variant="primary"
      type="submit"
      className="font-weight-bold signInBtn mt-2"
      onClick={() =>
      {
        window.location.assign(props.href);
      }}
    >
      {props.children}
    </Button>
  );
}

export interface SubPageProps
{
  className?: string;
}

export function SubPage(props: PropsWithChildren<SubPageProps>)
{
  const { ...containerProps } = props;

  return (
    <Container {...containerProps} />
  );
}

export const arrowClosedStyle = { width: '1em', height: '1em', fill: 'var(--yellow-color)' };
export const arrowOpenIconStyle = { transform: 'rotate(180deg)', ...arrowClosedStyle, fill: 'var(--blue-color)' };
export const infoboxIconStyle = { width: '1.4em', height: '1.4em', fill: 'var(--yellow-color)' };
export const infoboxIconActStyle = { width: '1.4em', height: '1.4em', fill: 'var(--blue-color)' };

export function FAQBox(props: PropsWithChildren<{ title: ReactNode }>)
{
  const [isOpen, setOpen] = useState(false);

  return (
    <div>
      <div
        className="my-3"
        style={{
          filter: 'drop-shadow(0px 5px 14.5px rgba(17, 56, 154, 0.2))',
          backgroundColor: '#ffffff',
        }}>
        <div
          onClick={() => setOpen(!isOpen)}
          className="d-flex justify-content-between fs-18 fw-500 py-3 px-3 cursor-pointer  dark-hover"
        >
          <div>
            {props.title}
          </div>
          <div>
            <ArrowDarkIcon className="svgStdIcon" style={isOpen ? arrowOpenIconStyle : arrowClosedStyle} />
          </div>
        </div>
      </div >
      <div className={`${isOpen ? '' : 'd-none'} px-3 fs-16 fw-400 pb-3 text-justify`}>
        {props.children}
      </div>
    </div>
  );
}

export function ScrollToElem(elemId: string, block?: ScrollLogicalPosition): void
{
  setTimeout(() =>
  {
    const el = document.getElementById(elemId);
    if (el && el.focus)
    {
      el.scrollIntoView({ block: block ?? 'center' });
      el.focus();
    }
  }, 300);
}

export function ListItem(props: PropsWithChildren<{}>)
{
  return (
    <div
      style={{
        marginTop: 22,
      }}
    >
      <div className="mx-2"
        style={{
          marginBottom: 2,
          display: 'inline-block',
          width: 9,
          height: 9,
          borderRadius: 5,
          backgroundColor: 'var(--yellow-color)'
        }}
      />
      <span className="ml-2">
        {props.children}
      </span>
    </div >
  );

}

export function BulletItem(props: PropsWithChildren<{ className?: string }>)
{
  return (
    <div className={`${props.className ?? 'mt-3'} d-flex align-items-center`}>
      <div className="mx-2"
        style={{

          display: 'inline-block',
          width: 9,
          height: 9,
          borderRadius: 5,
          backgroundColor: 'var(--yellow-color)',
        }}
      />
      <span className="ml-1" style={{ maxWidth: '90%  ' }}>
        {props.children}
      </span>
    </div >
  );

}

const stepStyle = {
  backgroundColor: 'var(--blue-color)',
  color: 'white',
  height: 47,
  width: 47,
  borderRadius: 24,
};

const stepStyle1 = {
};

export function StepBox(props: PropsWithChildren<{ stepNo: string }>)
{
  return (
    <div className="d-flex mt-3">
      <div>
        <div
          className="mr-2 va-center justify-content-center"
          style={stepStyle}
        >
          {props.stepNo}
        </div>
      </div>
      <div className="va-center" style={stepStyle1}>
        {props.children}
      </div>
    </div>
  );
}

export function IconStepBox(props: PropsWithChildren<{ icon: ReactNode }>)
{
  return (
    <div className="d-flex mt-3">
      <div
        className="mr-3 mb-3 mb-xl-0 va-center"
      >
        {props.icon}
      </div>
      <div style={stepStyle1}>
        {props.children}
      </div>
    </div>
  );
}

export function Question(props:
  {
    answer: DocBooleanField;
    matchesNeedsOption?: boolean;
  })
{
  const { answer, matchesNeedsOption } = props;
  return <div className="mb-4" style={{
    fontSize: 16,
    lineHeight: '35px',
    fontWeight: 600,
  }}>
    <div>
      {answer.fieldLabel}
    </div>
    <div className="d-flex justify-content-center">
      <RadioBoolean
        docField={answer}
      />
    </div>
    {matchesNeedsOption !== undefined &&
      !answer.isControlEmpty() &&
      answer.getValue() !== matchesNeedsOption &&
      <div className="mt-3">
        <Alert variant="danger" style={{ fontWeight: 100 }}>
          To ubezpieczenie nie spełni Twoich potrzeb.<br />
          W razie pytań zapraszamy do kontaktu pod
          numerem telefonu <a className="text-nowrap" href={'tel:' + helpPhone1}>{prettyPrintPLPhoneNumber(helpPhone1)}</a> lub <a className="text-nowrap" href={'tel:' + helpPhone2}>{prettyPrintPLPhoneNumber(helpPhone2)}</a>
        </Alert>
      </div>
    }
  </div>;
}
